/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type PulseAccordion } from 'glint/pulse';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type { ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import { tracked } from '@glimmer/tracking';
import type Router from '@ember/routing/router-service';

interface Args {
  accordion: PulseAccordion;
  onContinue?: () => void;
  onCasesChannel?: boolean;
}

interface Signature {
  Args: Args;
}

export default class Connect extends Component<Signature> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare notificationsService: $TSFixMe;

  @tracked showManageConnection = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.maybeNotifyConnectionSuccess();
  }

  private maybeNotifyConnectionSuccess() {
    let queryParams = this.router.currentRoute?.queryParams;
    if (queryParams?.connected === 'true' && this.salesforceConfig?.isConnected) {
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'standalone.deploy.salesforce.cases.connect.connect-section.success-notification',
          {
            instanceUrl: this.salesforceConfig?.instanceUrl,
          },
        ),
      );
    }
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get isComplete() {
    return this.salesforceConfig?.isSuccessfullyConnected || false;
  }

  get salesforceConnectionCard(): ConnectionOption {
    return {
      value: 'salesforce',
      icon: 'salesforce',
      label: this.intl.t(
        'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.label',
      ),
      description: this.intl.t(
        'standalone.deploy.salesforce.cases.connect.connect-section.card-description',
      ),
      isConnected: this.salesforceConfig?.isConnected,
      isAuthFailed: this.salesforceConfig?.isAuthFailed,
      stampConnected: this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.stamp-connected',
        {
          environmentLabel: this.salesforceConfig?.environmentLabel,
        },
      ),
      manage: () => {
        this.toggleManageConnection();
      },
      connect: () => {
        this.toggleManageConnection();
      },
    };
  }

  get redirectLocation(): string | undefined {
    return this.args.onCasesChannel ? undefined : 'intercom-messenger';
  }

  @action toggleManageConnection() {
    this.showManageConnection = !this.showManageConnection;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Connect::Connect': typeof Connect;
    'standalone/deploy/salesforce/cases/connect/connect': typeof Connect;
  }
}
